/**
 * Safe arithmetic expression evaluator для formula-полів.
 *
 * Підтримує:
 * - Числа (integer і float)
 * - +, -, *, /, parentheses
 * - Unary minus / unary plus
 * - {fieldKey} substitution (рядки з record values → числа)
 * - Whitelisted функції: round, floor, ceil, abs, max, min, sum, avg
 *   (varargs для max/min/sum/avg)
 *
 * NE підтримує: eval(), довільні функції, властивості, доступ до prototype.
 * Безпечно для UI — admin вводить вираз у Settings, frontend обчислює на read.
 *
 * Реалізація: recursive descent parser. ~110 LoC, без зовнішніх залежностей.
 */

const FUNCTIONS: Record<string, (...args: number[]) => number> = {
  round: (x = 0) => Math.round(x),
  floor: (x = 0) => Math.floor(x),
  ceil: (x = 0) => Math.ceil(x),
  abs: (x = 0) => Math.abs(x),
  max: (...xs) => (xs.length ? Math.max(...xs) : NaN),
  min: (...xs) => (xs.length ? Math.min(...xs) : NaN),
  sum: (...xs) => xs.reduce((a, b) => a + b, 0),
  avg: (...xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN),
};

/** Спробувати обчислити expression з підставленими значеннями.
 *  Повертає number при успіху, null при помилці парсингу / division by zero / missing vars. */
export function evaluateFormula(
  expression: string,
  vars: Record<string, unknown>,
): number | null {
  let pos = 0;
  const expr = expression;

  function peek(): string {
    return expr[pos] ?? '';
  }
  function next(): string {
    return expr[pos++] ?? '';
  }
  function skipSpace(): void {
    while (pos < expr.length && /\s/.test(peek())) pos++;
  }

  function parseFactor(): number {
    skipSpace();
    const c = peek();
    if (c === '(') {
      next();
      const v = parseExpr();
      skipSpace();
      if (peek() !== ')') throw new Error('expected )');
      next();
      return v;
    }
    if (c === '{') {
      next();
      let name = '';
      while (pos < expr.length && peek() !== '}') name += next();
      if (peek() !== '}') throw new Error('expected }');
      next();
      const v = vars[name];
      const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
      if (!Number.isFinite(n)) throw new Error(`invalid var {${name}}`);
      return n;
    }
    if (c === '-') {
      next();
      return -parseFactor();
    }
    if (c === '+') {
      next();
      return parseFactor();
    }
    // Function call: identifier '(' args ')'
    if (/[a-zA-Z_]/.test(c)) {
      let name = '';
      while (pos < expr.length && /[a-zA-Z_0-9]/.test(peek())) name += next();
      skipSpace();
      if (peek() !== '(') throw new Error(`expected ( after '${name}'`);
      next();
      const args: number[] = [];
      skipSpace();
      if (peek() !== ')') {
        args.push(parseExpr());
        skipSpace();
        while (peek() === ',') {
          next();
          args.push(parseExpr());
          skipSpace();
        }
      }
      if (peek() !== ')') throw new Error('expected )');
      next();
      const fn = FUNCTIONS[name];
      if (!fn) throw new Error(`unknown function '${name}'`);
      const result = fn(...args);
      if (!Number.isFinite(result)) throw new Error(`'${name}' returned non-finite`);
      return result;
    }
    let num = '';
    while (pos < expr.length && /[0-9.]/.test(peek())) num += next();
    if (!num) throw new Error('expected number');
    const n = parseFloat(num);
    if (!Number.isFinite(n)) throw new Error('invalid number');
    return n;
  }

  function parseTerm(): number {
    let v = parseFactor();
    skipSpace();
    while (peek() === '*' || peek() === '/') {
      const op = next();
      const r = parseFactor();
      if (op === '/' && r === 0) throw new Error('div by zero');
      v = op === '*' ? v * r : v / r;
      skipSpace();
    }
    return v;
  }

  function parseExpr(): number {
    let v = parseTerm();
    skipSpace();
    while (peek() === '+' || peek() === '-') {
      const op = next();
      const r = parseTerm();
      v = op === '+' ? v + r : v - r;
      skipSpace();
    }
    return v;
  }

  try {
    const result = parseExpr();
    skipSpace();
    if (pos < expr.length) throw new Error(`unexpected '${peek()}'`);
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}
